<template>
  <section v-if="!subgroupLoading">
    <PageTitle>
      <span v-html="groupName" />
    </PageTitle>
    <SwipeTab
      v-model="activeSubgroupId"
      @change="onSubgroupTabChange"
      :items="subgroupTabItems"
      :background-color="true"
      :loading="loading"
      :sticky="true"
      ref="swipeTab"
    />
    <PageImage
      v-if="coordinateImagePath && !loading"
      :src="coordinateImagePath | appendDomain"
      alt=""
    />
    <PageContent :top-sticked="true" :loading="loading">
      <Container>
        <div v-if="hasCoordinateButton" class="coordinate-button-wrapper">
          <router-link
            :to="$wcRouter.path({
              name: 'bridal-item.coordinate',
              params: { groupId: groupId }
            })"
            class="button waves-effect waves-light"
          >
            コーディネートを選びなおす
          </router-link>
        </div>
        <NoticeBox
          class="space-t-4"
          v-if="isUsageLimitOver"
          type="info"
          :icon-class="['fas', 'fa-lock']"
        >
          利用制限日を過ぎたため、変更できません
        </NoticeBox>
      </Container>
      <div v-show="videoUrl" class="bridal-item-subgroup-video-wrapper">
        <div id="bridal-item-subgroup-video" ref="video"></div>
      </div>
      <Container>
        <Accordion
          class="related-favorite-accordion"
          v-if="relatedFavoriteItemList.length"
          :hasHeader="true"
          title="関連するお気に入りアイテム"
          ref="relatedFavoriteAccordion"
        >
          <div
            v-for="groupRow in relatedFavoriteList"
            v-bind:key="groupRow.groupId"
          >
            <Headline :level="2">
              {{$domUtils.decode(groupRow.groupName)}}
            </Headline>
            <div
              v-for="subgroupRow in groupRow.subgroupList"
              v-bind:key="subgroupRow.subgroupId"
            >
              <Headline :level="3">
                {{$domUtils.decode(subgroupRow.subgroupName)}}
              </Headline>
              <ul class="related-favorite-item-list">
                <li
                  v-for="row in subgroupRow.itemList"
                  :key="row.bridal_item_id"
                >
                  <BridalItemCard
                    :id="`related-favorite-item-${row.bridal_item_id}`"
                    :item="relatedFavoriteItem(row.bridal_item_id)"
                    @click-image
                      ="showRelatedFavoriteModal(row.bridal_item_id)"
                  ></BridalItemCard>
                </li>
              </ul>
            </div>
          </div>
        </Accordion>
        <div class="bridal-item-list-wrapper">
          <div class="bridal-item-irregular" v-if="hasIrregularFilter">
            <a
              class="bridal-item-irregular-link"
              href="#"
              @click.prevent="onToggleIrregular"
            >
              <i
                class="bridal-item-irregular-icon"
                :class="{
                  'far fa-eye': getIrregular(),
                  'far fa-eye-slash': !getIrregular()
                }"
              ></i>
            </a>
          </div>
          <div v-if="itemList.length === 0">
            <NoticeBox
              type="error"
              :borderless="true"
              class="space-b-2 text-bold"
            >
              現在、アイテムがありません
            </NoticeBox>
            <p class="text-paragraph text-xs">
              現在表示されるアイテムがありません。
              <br class="mobile-only" />
              アイテムがある場合は、この画面に表示されます。
            </p>
          </div>
          <ul v-else class="bridal-item-list">
            <li v-for="row in itemList" :key="row.bridal_item_id">
              <BridalItemCard
                :id="`bridal-item-${row.bridal_item_id}`"
                :item="item(row.bridal_item_id)"
                :favoriteButtonShow
                  ="!item(row.bridal_item_id).simulation_target"
                :simulationButtonShow
                  ="item(row.bridal_item_id).simulation_target"
                :favoriteButtonDisabled="isUsageLimitOver"
                :simulationButtonDisabled="true"
                @click-image="showItemModal(row.bridal_item_id)"
                @toggle-favorite="onToggleFavorite"
              ></BridalItemCard>
            </li>
          </ul>
        </div>
      </Container>
    </PageContent>
    <SimpleModal id="bridal-item-modal" ref="bridalItemModal">
      <template #default>
        <BridalItemCard
          class="in-modal"
          :item="item(itemModalTargetId)"
          :favoriteButtonShow=!item(itemModalTargetId).simulation_target
          :simulationButtonShow="item(itemModalTargetId).simulation_target"
          :favoriteButtonDisabled="isUsageLimitOver"
          :simulationButtonDisabled="true"
          @toggle-favorite="onToggleFavorite"
        ></BridalItemCard>
      </template>
    </SimpleModal>
    <SimpleModal
      id="related-favorite-item-modal"
      ref="relatedFavoriteItemModal"
    >
      <template #default>
        <BridalItemCard
          class="in-modal"
          :item="relatedFavoriteItem(relatedFavoriteItemModalTargetId)"
        ></BridalItemCard>
      </template>
    </SimpleModal>
  </section>
</template>

<script>
import { renderError, renderNotFoundError } from '@/utils/error'
import { SCHEME, getDomain } from '@/api/theme'
import bridalItemApi from '@/api/bridal-item'
import SwipeTab from '@/components/SwipeTab.vue'
import NoticeBox from '@/components/NoticeBox'
import Accordion from '@/components/Accordion'
import SimpleModal from '@/components/SimpleModal'
import BridalItemCard from '@/components/BridalItemCard.vue'
import { getLocalStorage, saveLocalStorage,
  FIRST_ACCESSED, BRIDAL_ITEM_IRREGULAR } from '@/utils/local-storage'
import YoutubePlayer from 'youtube-player'
import queryString from 'query-string'

const storagePath = SCHEME + getDomain()
const COORDINATE_TYPE_NONE = 1
const COORDINATE_TYPE_CEREMONY = 2
const COORDINATE_TYPE_COMMON = 3
const RESPONSE_NO_SELECTED_COORDINATE = 483
const RESPONSE_USAGE_LIMIT_OVER = 490
const RESPONSE_SOMULATION_ADDED = 491

export default {
  metaInfo() {
    return {
      title: this.$store.getters['app/getMenuName']('bridal-item'),
      bodyAttrs: { class: 'bridal-item-show ' + this.groupIdClass }
    }
  },
  components: {
    SwipeTab,
    NoticeBox,
    Accordion,
    SimpleModal,
    BridalItemCard,
  },
  data() {
    return {
      groupId: this.$route.params.groupId,
      groupName: '',
      coordinateId: null,
      coordinateType: null,
      coordinateImagePath: null,
      selectedCoordinateRevision: 0,
      subgroupList: [],
      activeSubgroupId: 0,
      itemList: [],
      relatedFavoriteItemList: [],
      itemModalTargetId: 0,
      relatedFavoriteItemModalTargetId: 0,
      subgroupLoading: false,
      loading: false,
      player: null,
    }
  },
  computed: {
    groupIdClass() {
      return 'bridal-item-group-id-' + this.groupId
    },
    businessId() {
      return this.$store.getters['auth/businessId']
    },
    hasCoordinateButton() {
      return this.coordinateType == COORDINATE_TYPE_COMMON
        && !this.isUsageLimitOver
    },
    hasIrregularFilter() {
      return this.$store.getters['auth/isPlanner']
    },
    subgroupTabItems() {
      return this.subgroupList.map(row => ({
        key: row.bridal_item_subgroup_id,
        label: this.$domUtils.decode(row.bridal_item_subgroup_name),
      }))
    },
    videoUrl() {
      if (this.activeSubgroupId === 0) {
        return null
      }
      return this.subgroupList.find(
        row => row.bridal_item_subgroup_id === this.activeSubgroupId
      ).video_url || null
    },
    // アイテム一覧から指定したIDのアイテム1件を取り出し、
    // BridalItemCard.vueに渡せる形に整形
    item() {
      return function(id) {
        let result = {
          bridal_item_id: 0,
          bridal_item_name: '',
          image_path: '',
          bridal_item_description: '',
          retail_price: 0,
          alert_message: null,
          favorited: false,
          simulation_target: false,
          collage_target: false,
          revision: 0,
          loading: false,
        }
        const selectedItem = this.itemList.find(
          row => row.bridal_item_id === id
        )
        if (!selectedItem) {
          return result
        }
        result.bridal_item_id = selectedItem.bridal_item_id
        result.bridal_item_name = selectedItem.bridal_item_name
        result.image_path = selectedItem.image_path
        result.bridal_item_description = selectedItem.bridal_item_description
        result.retail_price = selectedItem.retail_price
        result.alert_message = selectedItem.alert_message
        result.favorited = selectedItem.favorited
        result.simulation_target = selectedItem.simulation_target
        result.revision = selectedItem.revision
        result.loading = selectedItem.loading

        return result
      }
    },
    // お気に入り一覧をグループ・サブグループ・アイテムの3階層に整形して返す
    // （グループ名、サブグループ名の見出しを表示するのに使用）
    relatedFavoriteList() {
      let result = []
      for (const row of this.relatedFavoriteItemList) {
        const groupId = row.bridal_item_group_id
        const subgroupId = row.bridal_item_subgroup_id
        if (result.find((row) => row.groupId === groupId) === undefined) {
          result.push({
            groupId: groupId,
            groupName: row.bridal_item_group_name,
            subgroupList: [],
          })
        }
        const groupIndex = result.length - 1
        if (
          result[groupIndex].subgroupList.find(
            (row) => row.subgroupId === subgroupId
          ) === undefined
        ) {
          result[groupIndex].subgroupList.push({
            subgroupId: subgroupId,
            subgroupName: row.bridal_item_subgroup_name,
            itemList: [],
          })
        }
        const subgroupIndex = result[groupIndex].subgroupList.length - 1
        result[groupIndex].subgroupList[subgroupIndex].itemList.push(row)
      }
      return result
    },
    // お気に入り一覧から指定したIDのアイテム1件を取り出し、
    // BridalItemCard.vueに渡せる形に整形
    relatedFavoriteItem() {
      return function(id) {
        let result = {
          bridal_item_id: 0,
          bridal_item_name: '',
          image_path: '',
          bridal_item_description: '',
          retail_price: 0,
          alert_message: null,
          favorited: false,
          simulation_target: false,
          collage_target: false,
          loading: false,
        }
        const selectedItem = this.relatedFavoriteItemList.find(
          row => row.bridal_item_id === id
        )
        if (!selectedItem) {
          return result
        }
        result.bridal_item_id = selectedItem.bridal_item_id
        result.bridal_item_name = selectedItem.bridal_item_name
        result.image_path = selectedItem.image_path
        result.bridal_item_description = selectedItem.bridal_item_description
        result.retail_price = selectedItem.retail_price
        result.alert_message = selectedItem.alert_message

        return result
      }
    },
    isUsageLimitOver() {
      return this.$store.getters['auth/isCustomer']
        && this.$store.getters['app/getUsageLimitFlag']
        && this.$store.getters['app/getUsageLimitDays'] === 0
    },
  },
  methods: {
    async fetchSubgroup() {
      this.subgroupLoading = true
      let response = null
      try {
        response = await bridalItemApi.getSubgroup(
          this.businessId,
          this.groupId
        )
      } catch (err) {
        renderError(err)
        return
      }
      this.groupName = response.bridal_item_group_name
      this.coordinateId = response.coordinate_id
      this.coordinateType = response.coordinate_type
      this.coordinateImagePath = response.coordinate_image_path
      this.selectedCoordinateRevision
        = response.selected_coordinate_revision
      this.subgroupList = response.bridal_item_subgroup_list
      this.activeSubgroupId = this.getActiveSubgroupId()

      // 存在しないサブグループIDの場合、404エラー画面に遷移
      if (this.activeSubgroupId === 0) {
        renderNotFoundError()
        return
      }

      // コーディネート選択済かチェック
      if (
        this.coordinateId === null
        && this.coordinateType !== COORDINATE_TYPE_NONE
      ) {
        this.onNoSelectedCoordinate()
        return
      }

      this.subgroupLoading = false
      // this.$refs.swipeTabやthis.$refs.videoが表示後に初期化する必要がある
      setTimeout(() => {
        this.$refs.swipeTab.initCarousel()
        this.initVideo()
      })

      // サブグループ取得後、アイテム取得
      this.loading = true
      this.fetchItem()
    },
    async fetchItem() {
      let response = null
      try {
        response = await bridalItemApi.getItem(
          this.businessId,
          this.activeSubgroupId,
          this.getIrregular(),
          this.coordinateId
        )
      } catch (err) {
        if (
          err.response
          && err.response.status === RESPONSE_NO_SELECTED_COORDINATE
        ) {
          this.onNoSelectedCoordinate()
          return
        }
        renderError(err)
        return
      }
      this.itemList = response.bridal_item_list.map(row => {
        // BridalItemCard.vueに必要な項目を追加
        row.loading = false
        return row
      })
      this.relatedFavoriteItemList = response.related_favorite_list

      this.loading = false
    },
    onNoSelectedCoordinate() {
      if (this.coordinateType === COORDINATE_TYPE_COMMON) {
        // 汎用コーディネート未選択の場合、コーディネート選択画面に遷移
        this.$wcRouter.replace({
          name: 'bridal-item.coordinate',
          params: { groupId: this.groupId },
        })
      } else if (this.coordinateType === COORDINATE_TYPE_CEREMONY) {
        // 挙式コーディネート未選択の場合、404エラー画面に遷移
        renderNotFoundError({replace: true})
      }
    },
    onSubgroupTabChange(subgroupId) {
      // コンテンツ読み込み中はタブ切り替え無効
      if (this.loading) {
        return false
      }

      window.scrollTo(0, 0)

      // URL変更（watchで検知される）
      this.loading = true
      this.$wcRouter.push({
        name: 'bridal-item.show',
        params: { groupId: this.groupId, subgroupId: subgroupId },
      })
    },
    getActiveSubgroupId() {
      // URLでサブグループIDが指定されてない場合、タブ先頭のIDを返す
      if (activeId === this.$route.params.subgroupId) {
        return this.subgroupList[0].bridal_item_subgroup_id
      }
      const activeId = Number(this.$route.params.subgroupId)
      // サブグループリストに存在しないIDの場合、0を返す
      if (
        this.subgroupList.filter(
          row => row.bridal_item_subgroup_id === activeId
        ).length === 0
      ) {
        return 0
      }
      return activeId
    },
    initVideo() {
      if (this.player !== null) {
        this.player.destroy()
      }
      if (this.videoUrl === null) {
        return
      }

      // 動画初期化
      this.player = YoutubePlayer(this.$refs.video, {
        // マスタの動画URLのクエリパラメータで指定されたオプションを反映
        playerVars: queryString.parse(new URL(this.videoUrl).search),
      })
      this.player.loadVideoByUrl(this.videoUrl)

      // ローカルストレージから初回アクセス済フラグを取得
      let firstAccessed = getLocalStorage(FIRST_ACCESSED, {})
      const pageKey = this.$route.name + '.' + this.activeSubgroupId
      if (firstAccessed[pageKey]) {
        // 初回アクセス済なら自動再生しない
        this.player.stopVideo()
        return
      }
      // ローカルストレージに初回アクセス済フラグを保存
      firstAccessed[pageKey] = true
      saveLocalStorage(FIRST_ACCESSED, firstAccessed)

      // 自動再生
      // クエリパラメータで「autoplay=1」を指定するやり方は
      // スマホでは効かないため、JSで行う必要がある
      this.player.playVideo()
    },
    showItemModal(id) {
      this.itemModalTargetId = id
      this.$refs.bridalItemModal.show()
    },
    showRelatedFavoriteModal(id) {
      this.relatedFavoriteItemModalTargetId = id
      this.$refs.relatedFavoriteItemModal.show()
    },
    async onToggleFavorite(item) {
      const favorited = !item.favorited
      // ON/OFF状態を変更し、API更新が終わるまでアイコン押下不可にする
      this.itemList = this.itemList.map(row => {
        if (row.bridal_item_id === item.bridal_item_id) {
          row.favorited = favorited
          row.loading = true
        }
        return row
      })
      try {
        const response = await bridalItemApi.updateFavorited(
          this.businessId,
          {
            bridal_item_id: item.bridal_item_id,
            coordinate_id: this.coordinateId || 0,
            favorited: favorited,
            revision: item.revision,
          }
        )
        // リビジョン更新し、アイコン押下可能に戻す
        this.itemList = this.itemList.map(row => {
          if (row.bridal_item_id === item.bridal_item_id) {
            row.revision = response.revision
            row.loading = false
          }
          return row
        })
      } catch (err) {
        renderError(err, {onErrorPopupClose: () => {
          // 利用制限エラーor見積追加済エラーの場合、画面リロード
          if (
            [
              RESPONSE_USAGE_LIMIT_OVER,
              RESPONSE_SOMULATION_ADDED,
            ].includes(err.response.status)
          ) {
            this.$router.go({
              path: this.$router.currentRoute.path,
              force: true,
            })
          }
        }})
        // ON/OFF状態を元に戻し、アイコン押下可能に戻す
        this.itemList = this.itemList.map(row => {
          if (row.bridal_item_id === item.bridal_item_id) {
            row.favorited = !favorited
            row.loading = false
          }
          return row
        })
      }
    },
    getIrregular() {
      if (!this.hasIrregularFilter) {
        return false
      }
      return getLocalStorage(BRIDAL_ITEM_IRREGULAR, false)
    },
    onToggleIrregular() {
      // コンテンツ読み込み中はタブ切り替え無効
      if (this.loading) {
        return false
      }

      window.scrollTo(0, 0)

      saveLocalStorage(
        BRIDAL_ITEM_IRREGULAR,
        !getLocalStorage(BRIDAL_ITEM_IRREGULAR, false)
      )
      this.loading = true
      this.fetchItem()
    },
  },
  filters: {
    appendDomain(val) {
      return storagePath + val
    },
  },
  created() {
    this.$withLoading(this.fetchSubgroup)
  },
  watch: {
    // URL変更を検知
    $route: {
      handler() {
        // グループ変更時、サブグループ取得＆アイテム取得をしなおす
        if (this.groupId !== this.$route.params.groupId) {
          this.groupId = this.$route.params.groupId
          this.fetchSubgroup()
          return
        }

        if (this.$refs.relatedFavoriteAccordion) {
          this.$refs.relatedFavoriteAccordion.close()
        }

        // ブラウザバックでのサブグループ変更時、タブ切替を自前でする
        // サブグループタブを先頭以外に切り替えた後、
        // 婚礼アイテムグループメニューで同一グループのリンク押下時もここを通る
        if (!this.loading) {
          this.loading = true
          this.activeSubgroupId = this.getActiveSubgroupId()
          // activeSubgroupIdの変更がSwipeTabのpropsに反映されるのを待つ
          setTimeout(() => {
            this.$refs.swipeTab.initCarousel()
          }, 0)
        }

        // サブグループ変更時、アイテムのみ取得しなおす
        this.initVideo()
        this.fetchItem()
      },
    },
  }
}
</script>